import { flowNodeNames, type FlowCallPath } from '@smarton-pbx/shared';
import { ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../../api';
import { t, tc } from '../../i18n';

/** A way out, in words. */
function exitText(type: string, exit: string | null, detail: string | null): string | null {
  if (exit === null || exit === 'next') return null;
  if (exit.startsWith('key:')) return t('pressed {key}', { key: exit.slice(4) });
  if (exit.startsWith('period:')) return exit.slice(7);
  const words: Record<string, string> = {
    invalid: detail ? t('wrong key ({key})', { key: detail }) : t('wrong key'),
    timeout: type === 'menu' ? t('no key') : t('waited too long'),
    nobody: t('nobody there'),
    callback: t('asked for a callback'),
    answered: t('answered'),
    noanswer: t('not answered'),
    open: t('open'),
    closed: t('closed'),
    on: t('on the list'),
    off: t('not on the list'),
  };
  return words[exit] ?? exit;
}

/** For admins and managers: the way a call went through its call flow. Nothing for other calls. */
export function CallPathCard({ callId }: { callId: string }) {
  const [path, setPath] = useState<FlowCallPath | null>(null);
  useEffect(() => {
    let current = true;
    api.flowCallPath(callId).then(
      (p) => current && setPath(p),
      () => undefined,
    );
    return () => {
      current = false;
    };
  }, [callId]);
  if (!path || path.steps.length === 0) return null;
  return (
    <div className="card">
      <div className="card-header">
        <h2>{t('Through the call flow')}</h2>
      </div>
      <div className="card-body">
        <ol className="call-path" data-testid="call-path">
          {path.steps.map((step, i) => {
            const how = exitText(step.type, step.exit, step.detail);
            return (
              <li key={i}>
                {i > 0 && <ChevronRight size={14} aria-hidden className="subtle" />}
                <span className="call-path-step">
                  {tc('element', flowNodeNames[step.type] ?? step.type)}
                  {how && <span className="subtle">: {how}</span>}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
